import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import { FileText, Plus, Download, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const STATUSES = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];
const ITEM_TYPES = ['subscription', 'usage', 'addon', 'credit', 'discount'];

/* ------------------------------------------------------------------ */
/*  Create Invoice Modal                                               */
/* ------------------------------------------------------------------ */
function CreateInvoiceModal({ open, onClose, onCreated }) {
  const [merchants, setMerchants] = useState([]);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [lineItems, setLineItems] = useState([
    { description: '', type: 'subscription', quantity: 1, unitPrice: 0 },
  ]);
  const [dueInDays, setDueInDays] = useState(7);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    adminAPI.getMerchants({ limit: 100 }).then(({ data }) => {
      setMerchants(data.data?.merchants || data.data || []);
    }).catch(() => {});
  }, [open]);

  const filteredMerchants = merchants.filter((m) => {
    const q = merchantSearch.toLowerCase();
    return (
      (m.businessName || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q)
    );
  });

  const selectedMerchantObj = merchants.find((m) => m._id === selectedMerchant);

  /* line-item helpers */
  const updateItem = (idx, field, value) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)),
    );
  };
  const addItem = () =>
    setLineItems((prev) => [
      ...prev,
      { description: '', type: 'subscription', quantity: 1, unitPrice: 0 },
    ]);
  const removeItem = (idx) =>
    setLineItems((prev) => prev.filter((_, i) => i !== idx));

  /* totals */
  const subtotal = lineItems.reduce(
    (sum, it) => sum + it.quantity * it.unitPrice,
    0,
  );
  const total = Math.max(0, subtotal - discount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMerchant) return toast.error('Select a merchant');
    if (lineItems.length === 0) return toast.error('Add at least one line item');
    setSubmitting(true);
    try {
      await adminAPI.createCustomInvoice({
        merchantId: selectedMerchant,
        lineItems: lineItems.map((it) => ({
          description: it.description,
          type: it.type,
          quantity: Number(it.quantity),
          unitPrice: Number(it.unitPrice),
        })),
        dueInDays: Number(dueInDays),
        discount: Number(discount),
        notes,
      });
      toast.success('Invoice created');
      onCreated();
      onClose();
    } catch {
      toast.error('Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--text)' }}>Create Invoice</h2>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* merchant search dropdown */}
          <label style={labelStyle}>Merchant</label>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <input
              className="form-input"
              style={{ width: '100%' }}
              placeholder="Search merchant..."
              value={selectedMerchantObj ? `${selectedMerchantObj.businessName} (${selectedMerchantObj.email})` : merchantSearch}
              onChange={(e) => {
                setMerchantSearch(e.target.value);
                setSelectedMerchant('');
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
            />
            {dropdownOpen && !selectedMerchant && (
              <div style={dropdownStyle}>
                {filteredMerchants.length === 0 && (
                  <div style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No merchants found</div>
                )}
                {filteredMerchants.map((m) => (
                  <div
                    key={m._id}
                    style={dropdownItem}
                    onClick={() => {
                      setSelectedMerchant(m._id);
                      setMerchantSearch('');
                      setDropdownOpen(false);
                    }}
                  >
                    <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>{m.businessName}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>{m.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* line items */}
          <label style={labelStyle}>Line Items</label>
          <div style={{ marginBottom: 14 }}>
            {lineItems.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: 140 }}>
                  {idx === 0 && <span style={fieldLabel}>Description</span>}
                  <input
                    className="form-input"
                    style={{ width: '100%' }}
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    required
                  />
                </div>
                <div style={{ flex: 1, minWidth: 110 }}>
                  {idx === 0 && <span style={fieldLabel}>Type</span>}
                  <select
                    className="form-input"
                    style={{ width: '100%' }}
                    value={item.type}
                    onChange={(e) => updateItem(idx, 'type', e.target.value)}
                  >
                    {ITEM_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ width: 70 }}>
                  {idx === 0 && <span style={fieldLabel}>Qty</span>}
                  <input
                    className="form-input"
                    style={{ width: '100%' }}
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))}
                    required
                  />
                </div>
                <div style={{ width: 100 }}>
                  {idx === 0 && <span style={fieldLabel}>Unit Price (&cent;)</span>}
                  <input
                    className="form-input"
                    style={{ width: '100%' }}
                    type="number"
                    min={0}
                    value={item.unitPrice}
                    onChange={(e) => updateItem(idx, 'unitPrice', Number(e.target.value))}
                    required
                  />
                </div>
                <div style={{ width: 80, textAlign: 'right', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)', paddingBottom: 8 }}>
                  ${((item.quantity * item.unitPrice) / 100).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  style={{ ...iconBtn, marginBottom: 4 }}
                  disabled={lineItems.length === 1}
                >
                  <Trash2 size={15} color="var(--red)" />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addItem} style={{ marginTop: 4 }}>
              <Plus size={14} style={{ marginRight: 4 }} /> Add Item
            </button>
          </div>

          {/* due in days + discount */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Due In Days</label>
              <input
                className="form-input"
                style={{ width: '100%' }}
                type="number"
                min={1}
                value={dueInDays}
                onChange={(e) => setDueInDays(Number(e.target.value))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Discount (&cent;)</label>
              <input
                className="form-input"
                style={{ width: '100%' }}
                type="number"
                min={0}
                value={discount}
                onChange={(e) => setDiscount(Number(e.target.value))}
              />
            </div>
          </div>

          {/* notes */}
          <label style={labelStyle}>Notes</label>
          <textarea
            className="form-input"
            style={{ width: '100%', minHeight: 60, marginBottom: 14, resize: 'vertical' }}
            placeholder="Optional notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* totals */}
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 16px', marginBottom: 18, border: '1px solid var(--border)' }}>
            <div style={totalRow}><span style={{ color: 'var(--text-muted)' }}>Subtotal</span><span>${(subtotal / 100).toFixed(2)}</span></div>
            {discount > 0 && (
              <div style={totalRow}><span style={{ color: 'var(--red)' }}>Discount</span><span>-${(discount / 100).toFixed(2)}</span></div>
            )}
            <div style={{ ...totalRow, fontWeight: 700, fontSize: '1rem', borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
              <span>Total</span><span>${(total / 100).toFixed(2)}</span>
            </div>
          </div>

          {/* actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    adminAPI
      .getInvoices({ status, page, limit: 20 })
      .then(({ data }) => {
        setInvoices(data.data.invoices);
        setTotal(data.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, page]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  /* download PDF */
  const downloadPDF = async (inv) => {
    try {
      const { data } = await adminAPI.downloadInvoicePDF(inv._id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${inv.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (_) {}
  };

  /* cycle status */
  const cycleStatus = async (inv) => {
    const idx = STATUSES.indexOf(inv.status);
    const next = STATUSES[(idx + 1) % STATUSES.length];
    try {
      await adminAPI.updateInvoiceStatus(inv._id, { status: next });
      setInvoices((prev) =>
        prev.map((i) => (i._id === inv._id ? { ...i, status: next } : i)),
      );
      toast.success(`Status changed to ${next}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const statusBadge = (s) => {
    const map = {
      paid: 'badge-green',
      sent: 'badge-blue',
      overdue: 'badge-red',
      draft: 'badge-gray',
      cancelled: 'badge-gray',
    };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  return (
    <div>
      {/* header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="page-subtitle">{total} total invoices</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link to="/admin/invoice-settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
            Invoice Settings
          </Link>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            <Plus size={15} style={{ marginRight: 4 }} /> Create Invoice
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* filter bar */}
        <div className="card" style={{ marginBottom: 14, display: 'flex', gap: 10 }}>
          <select
            className="form-input"
            style={{ width: 160 }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* table */}
        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div className="flex-center" style={{ padding: 60 }}>
              <div className="spinner" />
            </div>
          ) : (
            <div className="table-container">
              {invoices.length === 0 ? (
                <div className="empty-state" style={{ padding: 60 }}>
                  <FileText size={36} />
                  <h3>No invoices found</h3>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Merchant</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv._id}>
                        <td>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>
                            {inv.invoiceNumber}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/admin/merchants/${inv.merchantId?._id}`}
                            style={{
                              color: 'var(--primary)',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                            }}
                          >
                            {inv.merchantId?.businessName || '\u2014'}
                          </Link>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            {inv.merchantId?.email}
                          </div>
                        </td>
                        <td>
                          <strong>${(inv.total / 100).toFixed(2)}</strong>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.8rem' }}>
                            {format(new Date(inv.dueDate), 'MMM d, yyyy')}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => cycleStatus(inv)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            title="Click to change status"
                          >
                            {statusBadge(inv.status)}
                          </button>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {format(new Date(inv.createdAt), 'MMM d')}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => downloadPDF(inv)}
                            style={iconBtn}
                            title="Download PDF"
                          >
                            <Download size={15} color="var(--primary)" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* pagination */}
        {total > 20 && (
          <div className="flex-between" style={{ marginTop: 14 }}>
            <span className="text-muted">
              Page {page} of {Math.ceil(total / 20)}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page >= Math.ceil(total / 20)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* create invoice modal */}
      <CreateInvoiceModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={fetchInvoices}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline styles                                                      */
/* ------------------------------------------------------------------ */
const overlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modal = {
  background: 'var(--white)',
  borderRadius: 12,
  padding: '24px 28px',
  width: '100%',
  maxWidth: 700,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
};

const iconBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

const fieldLabel = {
  display: 'block',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  marginBottom: 2,
};

const totalRow = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.88rem',
  color: 'var(--text)',
  marginBottom: 4,
};

const dropdownStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  background: 'var(--white)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  maxHeight: 200,
  overflowY: 'auto',
  zIndex: 10,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
};

const dropdownItem = {
  padding: '8px 12px',
  cursor: 'pointer',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  alignItems: 'center',
};
